import axiosInstance, { ApiKeys, getApiUrl, logError } from '@cp/src/utils/ApiHelper';
import {
  setInitiateMockLoading,
  setInputValidations,
  setInputValidationsLoading,
  setFieldAllocationStatusLoading,
  setMockAllocationDetails,
  setMockRunsListLoading,
  setMockRunCapacity,
  setPreviousAllocationStatusLoading,
  setPreviousAllocationDetails,
  setUpdateMockRunStatusLoading,
  setMockRunsMap,
  setExperimentListLoading,
  setExperimentData,
  setExperimentsList,
  setIsLoading,
  setFailureReport,
  setMockControlsStatus
} from '../reducers/FieldAllocationSlice';
import { Dispatch } from '@reduxjs/toolkit';
import { OrchestrationStatus } from '../components/CaseExclusionExperiment/interfaces';
import {
  CreateExperimentPayload,
  UpdateExperimentPayload,
  UpdateMockRunStatusPayload
} from './interface';
import { MockStatus } from '../types';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import store from '@cp/src/store';
import { API_STATUS_CODE } from '@cp/src/utils/ApiHelperUtils';
import { ErrorMessage } from '../../auth/constants/AuthConstants';

export const fetchMockRunsList =
  (runHistoryFetchType: string, callback: (mockId: string) => void) => (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.GET_MOCK_RUN_HISTORY);
    dispatch(setMockRunsListLoading(true));
    axiosInstance
      .get(url, {
        params: {
          runHistoryFetchType
        }
      })
      .then(response => {
        if (response.status === API_STATUS_CODE.OK) {
          dispatch(setMockRunsMap(response?.data?.runHistoryItems));
          callback(response?.data?.runHistoryItems[0]?.allocationTrackerId);
        }
      })
      .catch(err => {
        logError(err);
      })
      .finally(() => {
        dispatch(setMockRunsListLoading(false));
      });
  };

export const fetchInputValidations = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_INPUT_VALIDATIONS);
  dispatch(setInputValidationsLoading(true));
  axiosInstance
    .get(url)
    .then(response => {
      if (response.status === API_STATUS_CODE.OK) {
        dispatch(setInputValidations({ data: response?.data, lastUpdated: Date.now() }));
      }
    })
    .catch(err => {
      logError(err);
    })
    .finally(() => {
      dispatch(setInputValidationsLoading(false));
    });
};

export const initiateMock = (successCallBack: () => void) => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.INITIATE_NEW_MOCK);
  dispatch(setInitiateMockLoading(true));
  axiosInstance
    .post(
      url,
      {},
      {
        headers: {
          donotHandleError: true
        }
      }
    )
    .then(response => {
      if (response.status === API_STATUS_CODE.OK) {
        successCallBack();
      }
    })
    .catch(err => {
      toast(err?.response?.data?.message, { type: 'error' });
    })
    .finally(() => {
      dispatch(setInitiateMockLoading(false));
    });
};

export const updateMockRunStatus =
  (payload: UpdateMockRunStatusPayload, successCallBack: () => void) => (dispatch: Dispatch) => {
    const { status, allocationTrackerId, agencyCapping } = payload;
    const url = getApiUrl(ApiKeys.UPDATE_MOCK_RUN_STATUS, { allocationTrackerId });
    dispatch(setUpdateMockRunStatusLoading(true));
    axiosInstance
      .patch(
        url,
        {
          status,
          agencyCapping
        },
        {
          headers: {
            donotHandleError: true
          }
        }
      )
      .then(res => {
        if (res?.status === API_STATUS_CODE.OK) {
          successCallBack();
        }
      })
      .catch(err => {
        toast(err?.response?.data?.message, { type: 'error' });
      })
      .finally(() => {
        dispatch(setUpdateMockRunStatusLoading(false));
      });
  };

export const fetchSingleMockRunDetails = (allocationTrackerId: string) => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_SINGLE_MOCK_RUN_DETAILS, { allocationTrackerId });
  dispatch(setFieldAllocationStatusLoading(true));
  axiosInstance
    .get(url)
    .then(response => {
      if (response.status === API_STATUS_CODE.OK) {
        dispatch(setMockAllocationDetails(response?.data));
        const updatedMockStatus = response?.data?.status;
        const mockRunsMap = store.getState().fieldAllocation?.mockRunsMap || {};
        const currentMockRun = mockRunsMap[allocationTrackerId];

        const approvedMockId =
          store.getState().fieldAllocation?.previousAllocationDetails?.allocationTrackerId || '';

        if (!!currentMockRun && currentMockRun?.status !== updatedMockStatus) {
          if (updatedMockStatus?.value === MockStatus.APPROVED) {
            mockRunsMap[approvedMockId] = {
              ...mockRunsMap[approvedMockId],
              status: currentMockRun?.status
            };
          }
          mockRunsMap[allocationTrackerId] = {
            ...mockRunsMap[allocationTrackerId],
            status: updatedMockStatus
          };
        }

        dispatch(setMockRunsMap(mockRunsMap));
      }
    })
    .catch(err => {
      logError(err);
    })
    .finally(() => {
      dispatch(setFieldAllocationStatusLoading(false));
    });
};

export const fetchMockRunCapacity = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.FETCH_MOCK_RUN_CAPACITY);
  axiosInstance
    .get(url)
    .then(response => {
      if (response.status === API_STATUS_CODE.OK) {
        dispatch(setMockRunCapacity(response?.data));
      }
    })
    .catch(err => {
      logError(err);
    });
};

export const fetchMockRunDetailsByStatus =
  (status: MockStatus, runHistoryFetchType: string) => (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.FETCH_MOCK_DETAILS_BY_STATUS);
    dispatch(setPreviousAllocationStatusLoading(true));
    axiosInstance
      .get(url, {
        params: {
          status,
          runHistoryFetchType
        }
      })
      .then(response => {
        if (response?.status === API_STATUS_CODE.OK) {
          dispatch(setPreviousAllocationDetails(response?.data));
        }
      })
      .catch(error => {
        logError(error);
      })
      .finally(() => {
        dispatch(setPreviousAllocationStatusLoading(false));
      });
  };

export const getAllExperiments = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.ALLOCATION_EXPERIMENTS);
  dispatch(setExperimentListLoading(true));

  axiosInstance
    .get(url, {
      headers: {
        donotHandleError: true
      }
    })
    .then(response => {
      if (response?.status === API_STATUS_CODE.OK) {
        dispatch(setExperimentsList(response?.data));
      }
    })
    .catch(error => {
      toast(error?.response?.data?.message || 'Failed to fetch experiments', { type: 'error' });
      logError(error);
    })
    .finally(() => {
      dispatch(setExperimentListLoading(false));
    });
};

export const createNewExperiment =
  (
    payload: CreateExperimentPayload,
    successCallBack: () => void,
    errorCallback?: (error: ErrorMessage) => void
  ) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.ALLOCATION_EXPERIMENTS);
    dispatch(setIsLoading(true));
    dispatch(setFailureReport(null));

    const requestData = {
      name: payload.name,
      details: payload.details
    };

    axiosInstance
      .post(url, requestData, {
        headers: {
          donotHandleError: true,
          'Content-Type': 'application/json'
        }
      })
      .then(response => {
        if (response?.status === API_STATUS_CODE.OK) {
          successCallBack();
        }
      })
      .catch(error => {
        toast(
          error?.response?.data?.message || error?.response?.data || 'Failed to create experiment!',
          { type: 'error' }
        );
        if (error?.response?.data?.errorReportUri) {
          dispatch(setFailureReport(error?.response?.data?.errorReportUri));
        }
        if (errorCallback) errorCallback(error?.response?.data);
        logError(error);
      })
      .finally(() => {
        dispatch(setIsLoading(false));
      });
  };

export const updateExperiment =
  (
    experimentId: string,
    payload: UpdateExperimentPayload,
    successCallBack: () => void,
    errorCallback?: (error: ErrorMessage) => void
  ) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.ALLOCATION_EXPERIMENT_BY_ID, { experimentId });
    dispatch(setIsLoading(true));
    dispatch(setFailureReport(null));

    const requestData: UpdateExperimentPayload = {};
    if (payload?.details) {
      requestData.details = payload.details;
    }
    if (payload?.status) {
      requestData.status = payload.status;
    }

    axiosInstance
      .put(url, requestData, {
        headers: {
          donotHandleError: true
        }
      })
      .then(response => {
        if (response?.status === API_STATUS_CODE.OK) {
          successCallBack();
        }
      })
      .catch(error => {
        toast(error?.response?.data?.message || 'Failed to update experiment!', { type: 'error' });
        if (error?.response?.data?.errorReportUri) {
          dispatch(setFailureReport(error?.response?.data?.errorReportUri));
        }
        if (errorCallback) errorCallback(error?.response?.data);
        logError(error);
      })
      .finally(() => {
        dispatch(setIsLoading(false));
      });
  };

export const getSingleExperimentData = (experimentId: string) => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.ALLOCATION_EXPERIMENT_BY_ID, { experimentId });
  dispatch(setIsLoading(true));
  axiosInstance
    .get(url)
    .then(response => {
      if (response?.status === API_STATUS_CODE.OK) {
        dispatch(setExperimentData(response?.data));
      }
    })
    .catch(error => {
      toast(error?.response?.data?.message || 'Failed to fetch experiment!', { type: 'error' });
      logError(error);
    })
    .finally(() => {
      dispatch(setIsLoading(false));
    });
};

export const updateFile =
  (file: File, experimentName: string, onFinally: () => void) => (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.UPDATE_EXPERIMENT_FILE);
    dispatch(setIsLoading(true));
    dispatch(setFailureReport(null));

    const formData = new FormData();
    formData.append('file', file);
    const parameters = {
      experimentName: experimentName
    };
    const jsonString = JSON.stringify(parameters);
    formData.append('parameters', jsonString);

    axiosInstance
      .put(url, formData, {
        headers: {
          orchestrationRequestType: 'FILE_PROCESSING_REQUEST',
          orchestrationRequestSubType: 'EXPERIMENT_ASSIGNMENT_REQUEST',
          donotHandleError: true
        }
      })
      .then(response => {
        if (response?.status === API_STATUS_CODE.OK) {
          toast('File uploaded successfully!', { type: 'success', delay: 500 });
        }
      })
      .catch(error => {
        const errorMessage =
          error?.response?.data?.message || error?.response?.data || 'Failed to upload file!';
        toast(errorMessage, { type: 'error', delay: 500 });

        logError(error);
      })
      .finally(() => {
        dispatch(setIsLoading(false));
        onFinally();
      });
  };

export const getMockControlsStatus = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_FIELD_ALLOCATIONS_STATUS);
  dispatch(setIsLoading(true));
  axiosInstance
    .get(url)
    .then(response => {
      if (response?.status === API_STATUS_CODE.OK) {
        dispatch(setMockControlsStatus(response?.data));
      }
    })
    .catch(error => {
      logError(error);
    })
    .finally(() => {
      dispatch(setIsLoading(false));
    });
};
